import {
  collection, addDoc, getDocs, query, orderBy, limit as qLimit,
  serverTimestamp, doc, updateDoc, getDoc, setDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';

// ── MESSAGES TO THE ADMINS ───────────────────────────────────────────────────
// The "Contact us" widget writes here; the admin dashboard reads it.
//
// A COLLECTION OF ITS OWN, not the chat system. Chat is a conversation between
// two USERS: it needs both uids, it writes a preview onto a parent document,
// and it notifies the recipient. Support has no second user — the recipient is
// "whoever is on duty" — and, more decisively, it must work for someone who is
// NOT SIGNED IN, which a chat thread keyed by two uids cannot represent.
//
// See firestore.rules for the shape this must satisfy. The validation there is
// the real contract: create is open to unauthenticated callers, so the rules
// constrain every field. Anything added here must be added there too, or the
// write is simply rejected.

export const SUPPORT_COLLECTION = 'supportMessages';

/** Matches the rules' cap. Enforced here so the UI can say so before sending. */
export const SUPPORT_MESSAGE_MAX = 2000;
export const SUPPORT_CONTACT_MAX = 200;

/** How long to make someone wait between messages, client-side. */
export const SUPPORT_COOLDOWN_MS = 60_000;
const LAST_SENT_KEY = 'tunify_support_last';

/**
 * Milliseconds still to wait, or 0 if a message may be sent now.
 *
 * A COURTESY, NOT A CONTROL. It lives in localStorage, so clearing storage or
 * opening a private window resets it — it exists to stop a frustrated person
 * sending the same thing five times, not to stop abuse. Rules cannot count
 * writes; App Check is the answer if this is ever attacked.
 */
export function supportCooldownLeft() {
  try {
    const last = Number(localStorage.getItem(LAST_SENT_KEY) || 0);
    if (!last) return 0;
    return Math.max(0, SUPPORT_COOLDOWN_MS - (Date.now() - last));
  } catch {
    return 0;                       // private mode: no throttle, no crash
  }
}

/**
 * Files a support message.
 *
 * @param {object}  input
 * @param {string}  input.message  what they wrote — required
 * @param {string} [input.contact] a phone or email to reply on
 * @param {string} [input.name]    what to call them
 * @param {string} [input.page]    where they were, for context
 */
export async function sendSupportMessage({ message, contact = '', name = '', page = '' }) {
  const body = String(message || '').trim();
  if (!body) {
    const err = new Error('empty');
    err.code = 'support/empty';
    throw err;
  }

  // The uid is read from the SDK rather than passed in, so a caller cannot
  // claim to be someone else — and the rules check it against the token
  // regardless. `null` when signed out, which is what the rules require: they
  // reject a missing field, so it has to be present and explicitly null.
  const uid = auth.currentUser?.uid ?? null;

  await addDoc(collection(db, SUPPORT_COLLECTION), {
    message: body.slice(0, SUPPORT_MESSAGE_MAX),
    contact: String(contact || '').trim().slice(0, SUPPORT_CONTACT_MAX),
    name: String(name || '').trim().slice(0, 120),
    // Which page they were on. Path only — never the query string, which can
    // carry ids and search terms nobody asked to file a support ticket about.
    page: String(page || '').slice(0, 300),
    fromUid: uid,
    // The rules require this to equal `request.time`, so it must be the server
    // sentinel; a client clock would be rejected.
    createdAt: serverTimestamp(),
    status: 'new',
  });

  try { localStorage.setItem(LAST_SENT_KEY, String(Date.now())); } catch { /* private mode */ }
}

/**
 * The newest messages, for the admin dashboard. Admin-only by the rules — a
 * non-admin caller gets a permission error rather than an empty list, which is
 * the honest failure.
 */
export async function listSupportMessages(max = 50) {
  const snap = await getDocs(query(
    collection(db, SUPPORT_COLLECTION),
    orderBy('createdAt', 'desc'),
    qLimit(max),
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Marks one handled (or back to new). Admin-only; see the rules. */
export function setSupportStatus(id, status) {
  return updateDoc(doc(db, SUPPORT_COLLECTION, id), { status });
}

// ── WHICH ACCOUNT RECEIVES "CONTACT ADMIN" ───────────────────────────────────
// `config/support` holds one field, `adminUid`. Set from the admin panel; read
// by every client so the Contact-admin button knows who to open a conversation
// with. See the rules for why it lives in the database rather than in .env.

const CONFIG_DOC = ['config', 'support'];

let _cache = null;
let _cachedAt = 0;
const CONFIG_TTL_MS = 5 * 60_000;

/**
 * The configured support uid, or '' when none is set.
 *
 * CACHED FOR FIVE MINUTES. Every client reads this to decide whether to show
 * one button, and it changes about once in the lifetime of the product — a
 * fresh read per Messages open would be a document get for nothing.
 * `{ force: true }` skips the cache, which is what the admin panel uses after
 * writing it.
 */
export async function getSupportAdminUid({ force = false } = {}) {
  if (!force && _cache !== null && Date.now() - _cachedAt < CONFIG_TTL_MS) return _cache;
  try {
    const snap = await getDoc(doc(db, ...CONFIG_DOC));
    _cache = snap.exists() ? (snap.data().adminUid || '') : '';
  } catch {
    // An unreadable config must not break Messages — the button simply does
    // not appear, which is the same as it not being configured.
    _cache = '';
  }
  _cachedAt = Date.now();
  return _cache;
}

/** Points support at `uid`. Admin-only by the rules. */
export async function setSupportAdminUid(uid) {
  await setDoc(doc(db, ...CONFIG_DOC), {
    adminUid: String(uid || ''),
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || '',
  });
  _cache = String(uid || '');
  _cachedAt = Date.now();
}

/**
 * The support uid already known, without a read — '' if it has not been
 * fetched yet.
 *
 * Exists so rendering code can ask synchronously. `otherParticipant` runs for
 * every row of the conversation list and cannot await; the async fetch is
 * kicked off when the Messages overlay mounts, so by the time rows paint this
 * is normally populated.
 */
export function cachedSupportAdminUid() {
  return _cache || '';
}
